import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} ingredientsContent - The element that displays the ingredient tags
 *
 * @extends {Component<Refs>}
 */

/**
 * A custom element that displays ingredient tags from the selected variant's
 * ingredients metafield (a list of `ingredient` metaobject entries).
 * Since variant metafields aren't part of the variant JSON payload the variant
 * picker already fetches, this component instead pulls its own freshly-rendered
 * markup out of the fetched product HTML by matching on `data-block-id`, the
 * same way `custom-variant-metafield` and `product-price` keep their own
 * content in sync.
 */
class CustomIngredients extends Component {
  requiredRefs = ['ingredientsContent'];

  /** @type {MutationObserver | undefined} */
  #contrastObserver;

  connectedCallback() {
    super.connectedCallback();
    // Pick dark or light label text per tag from its background image brightness, and
    // redo it whenever the tags are swapped for another variant.
    this.#applyContrast();
    this.#contrastObserver = new MutationObserver(() => this.#applyContrast());
    this.#contrastObserver.observe(this, { childList: true, subtree: true });
    // Listen on document so the block still hears variant changes when it's placed
    // outside the product-information section (e.g. in an accordion in another section).
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#contrastObserver?.disconnect();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the ingredient tags.
   * @param {import('@shopify/events').ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const ownSection = this.closest('.shopify-section, dialog');
    const isSameSection = ownSection?.contains(event.target);

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        // Fast path: every variant's tags are pre-rendered in a <template>, so swap them in
        // right away, in the same tick the price/media update.
        const variantId = detail.resource?.id;
        const template = variantId ? this.querySelector(`template[data-variant-id="${variantId}"]`) : null;
        if (template instanceof HTMLTemplateElement) {
          this.refs.ingredientsContent.replaceChildren(template.content.cloneNode(true));
          this.style.display = template.innerHTML.trim() ? 'block' : 'none';
          return;
        }

        // Fallback (e.g. switched to a different product): blocks in the variant picker's own
        // section are in the fetched HTML already; blocks in other sections re-render their own section.
        let newBlock = isSameSection ? this.#findBlock(html) : null;
        if (!newBlock && !isSameSection) {
          if (!variantId) return;
          newBlock = await this.#fetchBlock(variantId);
        }
        if (!newBlock) return;

        const newContent = newBlock.querySelector('[ref="ingredientsContent"]');
        if (newContent && this.refs.ingredientsContent) {
          this.refs.ingredientsContent.replaceWith(newContent);
        }

        // Hide the block when the newly selected variant has no ingredients.
        this.style.display = newBlock.style.display;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[custom-ingredients] Event promise rejected:', error);
      });
  };

  #applyContrast() {
    for (const tag of this.querySelectorAll('.ingredient-tag:not([data-contrast])')) {
      if (!(tag instanceof HTMLElement)) continue;
      const match = tag.style.getPropertyValue('--ingredient-tag-image').match(/url\(["']?(.+?)["']?\)/);
      if (!match) continue;
      tag.dataset.contrast = 'pending';
      imageLuminance(match[1]).then((luminance) => {
        // The image is drawn at 80% opacity over black (see .ingredient-tag::before).
        // Bright images get dark text. Mid-tone images read poorly with either colour,
        // so they keep white text and the image is dimmed a little more ('dim').
        const shown = luminance * 0.8;
        tag.dataset.contrast = shown > 0.3 ? 'dark' : shown > 0.12 ? 'dim' : 'light';
      });
    }
  }

  /**
   * @param {ParentNode} root
   * @returns {HTMLElement | null}
   */
  #findBlock(root) {
    const block = root.querySelector(`custom-ingredients[data-block-id="${this.dataset.blockId}"]`);
    return block instanceof HTMLElement ? block : null;
  }

  /**
   * Renders this block's section for the given variant via the Section Rendering API.
   * @param {string | number} variantId
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchBlock(variantId) {
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    const url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url);
    if (!response.ok) return null;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return this.#findBlock(doc);
  }
}

/** @type {Map<string, Promise<number>>} */
const luminanceCache = new Map();

/**
 * Average relative luminance (0 = black, 1 = white) of an image, sampled on a tiny canvas.
 * Resolves to 0 (keep light text) if the image can't be read.
 * @param {string} src
 * @returns {Promise<number>}
 */
function imageLuminance(src) {
  let result = luminanceCache.get(src);
  if (result) return result;

  result = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const size = 16;
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return resolve(0);
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        const linear = (/** @type {number} */ c) => {
          c /= 255;
          return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
        };
        let total = 0;
        for (let i = 0; i < data.length; i += 4) {
          total += 0.2126 * linear(data[i]) + 0.7152 * linear(data[i + 1]) + 0.0722 * linear(data[i + 2]);
        }
        resolve(total / (size * size));
      } catch {
        resolve(0);
      }
    };
    img.onerror = () => resolve(0);
    img.src = src;
  });
  luminanceCache.set(src, result);
  return result;
}

if (!customElements.get('custom-ingredients')) {
  customElements.define('custom-ingredients', CustomIngredients);
}
